import { useEffect, useMemo, useState } from 'react';
import { toEngineHandicap, type MatchReplayData, type GameReviewReport, type MoveBadge } from '@xiangqi/protocol';
import type { GameStateJson, XiangqiCore } from '@xiangqi/game-core';
import { api } from '../api.ts';
import { getCore } from '../wasm.ts';
import { Board } from './Board.tsx';
import { sfx } from '../audio/sfx.ts';
import { buildGameReviewFromReplay, describeAdvantage } from '../game/game-review.ts';
import { GameReviewModal, BADGE_CONFIG } from './GameReviewModal.tsx';

interface ReplayScreenProps {
  matchId: string;
  navigate(path: string): void;
}

export function ReplayScreen({ matchId, navigate }: ReplayScreenProps) {
  const [core, setCore] = useState<XiangqiCore | null>(null);
  const [data, setData] = useState<MatchReplayData | null>(null);
  const [states, setStates] = useState<GameStateJson[]>([]);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flipped, setFlipped] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReviewMode, setShowReviewMode] = useState(true);
  const [serverReview, setServerReview] = useState<GameReviewReport | null>(null);

  // Check URL query for review=1 and step=X
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const stepParam = params.get('step');
      if (stepParam) {
        const s = parseInt(stepParam, 10);
        if (!isNaN(s) && s >= 0) {
          setStep(s);
        }
      }
      if (params.get('review') === '1' && !stepParam) {
        setShowReviewModal(true);
      }
    }
  }, []);

  // Fetch server review report if available
  useEffect(() => {
    let cancelled = false;
    if (matchId.startsWith('local-')) return;
    api
      .review(matchId)
      .then((res) => {
        if (!cancelled && res?.report) {
          setServerReview(res.report);
        }
      })
      .catch(() => {
        // Fallback to local computation
      });
    return () => {
      cancelled = true;
    };
  }, [matchId]);

  // Load wasm core and replay data
  useEffect(() => {
    let cancelled = false;
    const fetchReplay = async (): Promise<MatchReplayData> => {
      if (matchId.startsWith('local-')) {
        const localRaw = sessionStorage.getItem(`xq_replay_${matchId}`);
        if (localRaw) {
          return JSON.parse(localRaw) as MatchReplayData;
        }
      }
      try {
        const res = await api.replay(matchId);
        return res.replay;
      } catch (err) {
        const localRaw = sessionStorage.getItem(`xq_replay_${matchId}`);
        if (localRaw) {
          return JSON.parse(localRaw) as MatchReplayData;
        }
        throw err;
      }
    };

    Promise.all([getCore(), fetchReplay()])
      .then(([c, replay]) => {
        if (cancelled) return;
        setCore(c);
        setData(replay);

        // Precompute board states for each move
        try {
          const engineHandicap = toEngineHandicap(replay.ruleset, replay.handicap);
          let current = c.newGame(replay.ruleset, BigInt(replay.seed || '0'), engineHandicap);
          const computed: GameStateJson[] = [current];

          for (const m of replay.moves) {
            try {
              const res = c.applyMove(current, { from: m.from, to: m.to });
              current = res.state;
              computed.push(current);
            } catch {
              // If move fails in engine, keep current
              computed.push(current);
            }
          }
          setStates(computed);
        } catch (err) {
          console.error('Failed to precompute states:', err);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Không tải được ván cờ');
      });

    return () => {
      cancelled = true;
    };
  }, [matchId]);

  // Auto-play timer
  useEffect(() => {
    if (!playing) return undefined;
    const interval = setInterval(() => {
      setStep((curr) => {
        if (curr >= states.length - 1) {
          setPlaying(false);
          return curr;
        }
        sfx.playMove();
        return curr + 1;
      });
    }, 1200);
    return () => clearInterval(interval);
  }, [playing, states.length]);

  const reviewReport = useMemo(() => {
    if (serverReview) return serverReview;
    if (!data) return null;
    try {
      return buildGameReviewFromReplay(data);
    } catch (err) {
      console.error('Failed to build review:', err);
      return null;
    }
  }, [serverReview, data]);

  if (error) {
    return (
      <main className="page">
        <div className="banner error">{error}</div>
        <button onClick={() => navigate('/history')}>Về lịch sử đấu</button>
      </main>
    );
  }

  if (!data || states.length === 0 || !core) {
    return (
      <main className="page">
        <p className="waiting">Đang chuẩn bị ván xem lại…</p>
        <button className="secondary-btn" onClick={() => navigate('/history')} style={{ marginTop: 12 }}>
          ← Về lịch sử đấu
        </button>
      </main>
    );
  }

  const currentState = states[step] ?? states[0];
  if (!currentState) {
    return (
      <main className="page">
        <p className="waiting">Đang chuẩn bị ván xem lại…</p>
      </main>
    );
  }

  const prevMove = step > 0 && data.moves ? data.moves[step - 1] : undefined;
  const lastMove = prevMove ? { from: prevMove.from, to: prevMove.to } : null;

  const goToStep = (target: number) => {
    const clamped = Math.max(0, Math.min(states.length - 1, target));
    if (clamped !== step) {
      setStep(clamped);
      sfx.playMove();
    }
  };

  return (
    <main className="page replay-page">
      <header className="replay-header">
        <button className="back-btn" onClick={() => navigate('/history')}>
          ← Quay lại
        </button>
        <div className="replay-title">
          <h2>Xem lại ván đấu</h2>
          <span className="replay-match-id">ID: {matchId.slice(0, 8)}</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="review-toggle-btn"
            onClick={() => setShowReviewModal(true)}
            title="Xem báo cáo phân tích AI toàn diện"
            style={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
              border: '1px solid #60a5fa',
              color: '#fff',
              fontWeight: 700,
              fontSize: '13px',
              padding: '6px 12px',
              borderRadius: '8px',
            }}
          >
            📊 Báo cáo AI
          </button>
          <button
            className="flip-btn"
            onClick={() => setShowReviewMode(!showReviewMode)}
            title="Bật/Tắt thanh đánh giá AI"
          >
            {showReviewMode ? '🔍 Ẩn AI bar' : '🔍 Hiện AI bar'}
          </button>
          <button
            className="flip-btn"
            onClick={() => setFlipped(!flipped)}
            title="Đảo góc nhìn bàn cờ"
          >
            🔄 Đảo góc
          </button>
        </div>
      </header>

      {/* Players summary */}
      <div className="replay-players-bar">
        <div className={`replay-player red ${data.result?.winner === 'red' ? 'winner' : ''}`}>
          <span className="dot red on" />
          <strong>{data.players.red.display_name}</strong> (Đỏ)
          {data.result?.winner === 'red' && <span className="crown"> 👑 Thắng</span>}
        </div>
        <div className="replay-vs">VS</div>
        <div className={`replay-player black ${data.result?.winner === 'black' ? 'winner' : ''}`}>
          <span className="dot black on" />
          <strong>{data.players.black.display_name}</strong> (Đen)
          {data.result?.winner === 'black' && <span className="crown"> 👑 Thắng</span>}
        </div>
      </div>

      {/* Eval / Win Rate Bar */}
      {showReviewMode && reviewReport && (() => {
        const evalPt = step < reviewReport.evalTrajectory.length ? reviewReport.evalTrajectory[step] : reviewReport.evalTrajectory[reviewReport.evalTrajectory.length - 1];
        const redWinRate = evalPt ? evalPt.winRateRed : 50;
        const blackWinRate = Math.round((100 - redWinRate) * 10) / 10;
        const cp = evalPt ? evalPt.evalCp : 0;
        const evalText = step === 0 ? 'Bắt đầu ván cờ (Cân bằng)' : describeAdvantage(cp);

        return (
          <div className="replay-eval-card">
            <div className="eval-meta-row">
              <span className="eval-stat red">
                Đỏ {redWinRate}%
              </span>
              <span className="eval-desc-pill">{evalText}</span>
              <span className="eval-stat black">
                Đen {blackWinRate}%
              </span>
            </div>
            <div className="eval-progress-bar">
              <div className="eval-progress-fill red" style={{ width: `${redWinRate}%` }} />
              <div className="eval-progress-fill black" style={{ width: `${blackWinRate}%` }} />
            </div>
          </div>
        );
      })()}

      {/* Board */}
      {(() => {
        const currentMoveReview = showReviewMode && reviewReport && step > 0 ? reviewReport.moves[step - 1] : undefined;
        const hintMove = currentMoveReview?.bestAlternative
          ? { from: currentMoveReview.bestAlternative.from, to: currentMoveReview.bestAlternative.to }
          : null;

        return (
          <div className="replay-board-wrap">
            <Board
              state={currentState}
              core={core}
              yourSide={flipped ? 'black' : 'red'}
              interactive={false}
              lastMove={lastMove}
              hintMove={hintMove}
            />
          </div>
        );
      })()}

      {/* Playback Controls */}
      <div className="replay-controls">
        <div className="replay-step-info">
          Nước đi: <strong>{step}</strong> / {states.length - 1}
        </div>

        <div className="replay-buttons-row">
          <button
            className="replay-btn"
            onClick={() => goToStep(0)}
            disabled={step === 0}
            title="Về đầu ván"
          >
            ⏮
          </button>
          <button
            className="replay-btn"
            onClick={() => goToStep(step - 1)}
            disabled={step === 0}
            title="Lùi một nước"
          >
            ◀
          </button>
          <button
            className={`replay-btn play-btn ${playing ? 'active' : ''}`}
            onClick={() => setPlaying(!playing)}
            title={playing ? 'Tạm dừng' : 'Tự động phát'}
          >
            {playing ? '⏸ Tạm dừng' : '▶ Tự động phát'}
          </button>
          <button
            className="replay-btn"
            onClick={() => goToStep(step + 1)}
            disabled={step >= states.length - 1}
            title="Tiến một nước"
          >
            ▶
          </button>
          <button
            className="replay-btn"
            onClick={() => goToStep(states.length - 1)}
            disabled={step >= states.length - 1}
            title="Đến cuối ván"
          >
            ⏭
          </button>
        </div>
      </div>

      {/* AI Coach Commentary Box */}
      {showReviewMode && reviewReport && (() => {
        const currentMoveReview = step > 0 ? reviewReport.moves[step - 1] : undefined;
        if (!currentMoveReview) {
          return (
            <div className="replay-coach-card intro">
              <div className="coach-card-header">
                <span className="coach-bot-icon">🤖</span>
                <span className="coach-heading">Trợ lý Huấn luyện viên AI</span>
                <button className="coach-summary-link" onClick={() => setShowReviewModal(true)}>
                  Báo cáo tổng kết 📊
                </button>
              </div>
              <p className="coach-text-desc">
                Bấm vào các nước đi hoặc bấm <strong>▶ Tự động phát</strong> để xem biến động điểm, nước tối ưu gợi ý và chiến lược phân tích theo từng nước cờ!
              </p>
              {reviewReport.criticalTurningPoint && (
                <div className="coach-turning-point-shortcut">
                  <button
                    className="turning-point-jump-btn"
                    onClick={() => goToStep(reviewReport.criticalTurningPoint!.ply)}
                  >
                    ⚡ Xem bước ngoặt quyết định (Nước {reviewReport.criticalTurningPoint.moveNumber}: {reviewReport.criticalTurningPoint.notationVi})
                  </button>
                </div>
              )}
            </div>
          );
        }

        const badgeCfg = BADGE_CONFIG[currentMoveReview.badge as MoveBadge] ?? BADGE_CONFIG.good;
        const isRed = step % 2 === 1;

        return (
          <div className="replay-coach-card">
            <div className="coach-card-header">
              <div className="coach-title-group">
                <span className="coach-bot-icon">🤖</span>
                <span className="coach-heading">
                  Nước {step} ({isRed ? 'Đỏ' : 'Đen'}): <strong>{currentMoveReview.notationVi || data.moves[step - 1]?.notation || `Nước ${step}`}</strong>
                </span>
              </div>
              <div className="coach-header-right">
                {currentMoveReview.strategicTag && (
                  <span className="coach-tag-pill">{currentMoveReview.strategicTag}</span>
                )}
                <div
                  className="coach-badge-tag"
                  style={{
                    backgroundColor: `${badgeCfg.color}25`,
                    borderColor: badgeCfg.color,
                    color: badgeCfg.color,
                  }}
                >
                  <span className="badge-tag-icon">{badgeCfg.icon}</span>
                  <span className="badge-tag-name">{badgeCfg.label}</span>
                </div>
              </div>
            </div>

            <p className="coach-text-desc">{currentMoveReview.aiCommentary}</p>

            {/* Strategic Details: Why wrong, Best alternative, and Correct strategy */}
            {currentMoveReview.whyWrong && (
              <div className="coach-strategic-item wrong">
                <span className="strategic-label">❌ Tại sao sai:</span>
                <span className="strategic-text">{currentMoveReview.whyWrong}</span>
              </div>
            )}

            {currentMoveReview.bestAlternative && (
              <div className="coach-strategic-item alternative">
                <span className="strategic-label">✅ Nước tối ưu:</span>
                <span className="strategic-text">
                  <strong className="alt-move-name">{currentMoveReview.bestAlternative.notationVi}</strong>
                  {currentMoveReview.bestAlternative.explanation && (
                    <span className="alt-move-desc"> — {currentMoveReview.bestAlternative.explanation}</span>
                  )}
                  <span className="hint-arrow-text"> (Được vẽ mũi tên xanh chỉ dẫn trên bàn cờ)</span>
                </span>
              </div>
            )}

            {currentMoveReview.correctStrategy && (
              <div className="coach-strategic-item strategy">
                <span className="strategic-label">💡 Chiến lược đúng:</span>
                <span className="strategic-text">{currentMoveReview.correctStrategy}</span>
              </div>
            )}

            <div className="coach-sub-metrics">
              <span>
                Thế trận:{' '}
                <strong>
                  {currentMoveReview.advantageDescription || (currentMoveReview.evalDeltaCp >= 0 ? 'Ổn định' : 'Bất lợi')}
                </strong>
              </span>
              <span>
                Độ chính xác trung bình: <strong>{isRed ? reviewReport.redPerformance.accuracy : reviewReport.blackPerformance.accuracy}%</strong>
              </span>
            </div>
          </div>
        );
      })()}

      {/* Move Notation List */}
      <div className="replay-moves-list">
        <h4>Biên bản ván đấu</h4>
        <div className="moves-scroll">
          {data.moves.map((m, idx) => {
            const isCurrent = step === idx + 1;
            const round = Math.floor(idx / 2) + 1;
            const isRedMove = idx % 2 === 0;
            const moveReview = reviewReport?.moves[idx];
            const badgeCfg = moveReview ? (BADGE_CONFIG[moveReview.badge as MoveBadge] ?? null) : null;
            const isTurningPoint = reviewReport?.criticalTurningPoint?.ply === idx + 1;
            const isBlunder = moveReview?.badge === 'blunder';

            return (
              <button
                key={idx}
                className={`move-chip ${isCurrent ? 'active' : ''} ${isRedMove ? 'red' : 'black'} ${isBlunder ? 'has-blunder' : ''} ${isTurningPoint ? 'is-turning-point' : ''}`}
                onClick={() => goToStep(idx + 1)}
                title={
                  isTurningPoint
                    ? `⚡ BƯỚC NGOẶT: ${moveReview?.aiCommentary || ''}`
                    : moveReview
                      ? `${badgeCfg?.label}: ${moveReview.aiCommentary}`
                      : undefined
                }
              >
                {isRedMove ? `${round}. ` : ''}
                {m.notation || `Nước ${idx + 1}`}
                {isTurningPoint ? (
                  <span className="move-chip-turning-point" title="Bước ngoặt ván đấu">⚡</span>
                ) : (
                  badgeCfg && (
                    <span
                      className="move-chip-badge"
                      style={{ color: badgeCfg.color }}
                    >
                      {badgeCfg.icon}
                    </span>
                  )
                )}
              </button>
            );
          })}
        </div>
      </div>

      {showReviewModal && reviewReport && (
        <GameReviewModal
          report={reviewReport}
          onClose={() => setShowReviewModal(false)}
          onViewReplay={(targetStep) => {
            setShowReviewModal(false);
            if (typeof targetStep === 'number' && targetStep >= 0 && targetStep < states.length) {
              setStep(targetStep);
            }
          }}
        />
      )}
    </main>
  );
}
